import { DecimalPipe } from '@angular/common';
import { CONFIG } from 'app/core/config';
import { MaintenanceStrategyTabIndex } from 'app/runnable-models/reliability-centered-maintenance/maintenance-analysis/maintenance-strategy-tab-index';
import { ColumnOutputType } from '../../../../component-library/table/column-output-type';
import { TableColumn } from '../../../../component-library/table/table-column';
import { TableConfig } from '../../../../component-library/table/table-config';
import { Widget } from '../../../../viewmodels/dashboard/widget';
import { WidgetParameter } from '../../../../viewmodels/dashboard/widget-parameter';
import { PlannedMaintenanceDistributionFrequency } from '../widget-parameters/planned-maintenance-distribution-frequency';
import { ValueSource } from '../widget-parameters/value-source.enum';
import { ValueType } from '../widget-parameters/value-type.enum';
import { ItemizedWidgetData } from './itemized-widget-data';

export class PlannedMaintenanceDistributionItemizedWidgetData extends ItemizedWidgetData {
  private frequencies = [
    { value: PlannedMaintenanceDistributionFrequency.OneWeek, label: '1 Week' },
    { value: PlannedMaintenanceDistributionFrequency.OneMonth, label: '1 Month' },
    { value: PlannedMaintenanceDistributionFrequency.ThreeMonths, label: '3 Months' },
    { value: PlannedMaintenanceDistributionFrequency.SixMonths, label: '6 Months' },
    { value: PlannedMaintenanceDistributionFrequency.TwelveMonths, label: '12 Months' },
    { value: PlannedMaintenanceDistributionFrequency.TwentyFourMonths, label: '24 Months' },
    { value: PlannedMaintenanceDistributionFrequency.SixtyMonths, label: '60 Months' },
    { value: PlannedMaintenanceDistributionFrequency.Other, label: 'Other' },
  ];

  protected createColumns(): TableColumn[] {
    const nameColumn = new TableColumn('Asset Name', 'OrgTreeNodeName');
    nameColumn.outputType = ColumnOutputType.Link;
    nameColumn.linkClickEvent = this.navigateToHierarchyItem;

    const activityColumn = new TableColumn('PM Activity', 'Reference');
    activityColumn.outputType = ColumnOutputType.Link;
    activityColumn.linkClickEvent = this.navigateToMaintenanceStrategy;

    const frequencyColumn = new TableColumn('Frequency (Months)', 'Frequency');
    frequencyColumn.formatterPipe = DecimalPipe;
    frequencyColumn.formatterPipeArgs = ['1.1-1'];

    return [nameColumn, activityColumn, frequencyColumn];
  }

  public setFilterParameter(widget: Widget, widgetParameter: WidgetParameter, reset: boolean): void {
    const paramIndex = widget.widgetItemizedDataParameters.findIndex(w => w.valueSource === ValueSource.PlannedMaintenanceDistributionEnum);

    if (paramIndex > -1) {
      if (!reset) {
        widget.widgetItemizedDataParameters[paramIndex] = widgetParameter;
        return;
      }
      widget.widgetItemizedDataParameters.splice(paramIndex, 1);
      return;
    }
    if (!reset && widgetParameter.valueSource === ValueSource.PlannedMaintenanceDistributionEnum) {
      widget.widgetItemizedDataParameters.push(widgetParameter);
    }
  }

  public createTableConfig(): TableConfig {
    const tableConfig = super.initializeTableConfig();
    tableConfig.columns = this.createColumns();
    return tableConfig;
  }

  public createOverallTableHeaderColumn(): TableColumn {
    const headerColumn = new TableColumn('Frequency', 'label');
    headerColumn.outputType = ColumnOutputType.Link;
    headerColumn.linkClickEvent = this.frequencyColumnDrillDown;
    return headerColumn;
  }

  private frequencyColumnDrillDown = (data: any) => {
    const widgetParam = new WidgetParameter();
    widgetParam.widgetId = data.widgetId;
    widgetParam.valueSource = ValueSource.PlannedMaintenanceDistributionEnum;
    widgetParam.valueType = ValueType.Int;
    widgetParam.displayName = data.label;

    const frequency = this.frequencies.find(x => x.label === data.label);

    widgetParam.intValue = frequency ? frequency.value : -1;

    this.viewDataTableService.setItemizedDataWidgetParam.next(widgetParam);
  };

  private navigateToHierarchyItem = (data: any) => {
    if (data.OrgTreeNodeId) {
      this.router.navigate([this.authService.urlCatalog + CONFIG.routes.hierarchy.item.root(data.orgTreeNodeId)]);
    }
  };

  private navigateToMaintenanceStrategy = (data: any) => {
    if (data.OrgTreeNodeId) {
      this.router.navigate([this.authService.urlCatalog + CONFIG.routes.mo.maintenanceStrategies, data.OrgTreeNodeId], {
        queryParams: { tabIndex: MaintenanceStrategyTabIndex.Analysis },
      });
    }
  };
}
